import { useState, useRef, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useMetasContext } from '../../contexts/MetasContext'
import { useGastosContext } from '../../contexts/GastosContext'
import { useTheme } from '../../contexts/ThemeContext'
import { useProfileContext } from '../../contexts/ProfileContext'
import { supabase } from '../../lib/supabase'
import { fonts } from '../../lib/themes'
import { getJourneyMessage } from '../../lib/metaJourneys'
import ProgressRing from '../ui/ProgressRing'
import MetaTipoPicker from '../ui/MetaTipoPicker'
import { IcTarget } from '../ui/Icons'

// ── Constants ─────────────────────────────────────────────────────────────────

const EMOJIS = ['✈️','🏠','💻','👗','🏖️','💍','🌱','⭐','🎓','🚗','🎸','🌊']
const COLORES = ['terra','sage','gold','sky','lav','mauve']

const FECHA_PRESETS = [
  { label: '1 semana',  days: 7    },
  { label: '2 semanas', days: 14   },
  { label: '1 mes',     months: 1  },
  { label: '3 meses',   months: 3  },
  { label: '6 meses',   months: 6  },
  { label: '1 año',     months: 12 },
  { label: '3 años',    months: 36 },
  { label: '5 años',    months: 60 },
]

const FRECUENCIAS = [
  { value: 'semanal',    label: 'semanal'    },
  { value: 'mensual',    label: 'mensual'    },
  { value: 'trimestral', label: 'trimestral' },
  { value: 'semestral',  label: 'semestral'  },
  { value: 'anual',      label: 'anual'      },
]

const MESES = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic']

const NOMBRE_LABELS = {
  meta:       '¿cuál es tu sueño?',
  viaje:      '¿a dónde vas?',
  proyecto:   '¿qué proyecto?',
  compromiso: '¿qué debes?',
  guardado:   '¿para qué ahorras cíclicamente?',
  fondo:      '¿cómo llamas tu reserva?',
}

const NOMBRE_PLACEHOLDERS = {
  meta:       'ej: comprar una laptop, reparar mi carro...',
  viaje:      'ej: viaje a Japón, excursión por Europa...',
  proyecto:   'ej: lanzar mi tienda online, remodelar la cocina...',
  compromiso: 'ej: tarjeta de crédito, pago del carro...',
  guardado:   'ej: fondo navideño, seguro anual del carro...',
  fondo:      'ej: fondo de emergencia, retiro...',
}

const OBJETIVO_LABELS = {
  meta:       'monto objetivo',
  viaje:      'presupuesto total del viaje',
  proyecto:   'presupuesto total del proyecto',
  compromiso: 'saldo de deuda',
  guardado:   'monto por ciclo',
  fondo:      'meta de reserva',
}

const MODAL_TITLES = {
  meta:       'nueva meta',
  viaje:      'nuevo viaje',
  proyecto:   'nuevo proyecto',
  compromiso: 'nuevo compromiso',
  guardado:   'nuevo guardado',
  fondo:      'nuevo fondo',
}

const DEFAULT_CATS = {
  viaje: [
    { nombre: 'Vuelos',      emoji: '✈️', presupuesto: '', orden: 0 },
    { nombre: 'Hospedaje',   emoji: '🏨', presupuesto: '', orden: 1 },
    { nombre: 'Comida',      emoji: '🍽️', presupuesto: '', orden: 2 },
    { nombre: 'Actividades', emoji: '🎭', presupuesto: '', orden: 3 },
    { nombre: 'Compras',     emoji: '🛍️', presupuesto: '', orden: 4 },
    { nombre: 'Otro',        emoji: '📦', presupuesto: '', orden: 5 },
  ],
  proyecto: [
    { nombre: 'Equipo',          emoji: '👥', presupuesto: '', orden: 0 },
    { nombre: 'Legal/Trámites',  emoji: '⚖️', presupuesto: '', orden: 1 },
    { nombre: 'Marketing',       emoji: '📣', presupuesto: '', orden: 2 },
    { nombre: 'Tecnología',      emoji: '💻', presupuesto: '', orden: 3 },
    { nombre: 'Espacio',         emoji: '🏢', presupuesto: '', orden: 4 },
    { nombre: 'Otro',            emoji: '📦', presupuesto: '', orden: 5 },
  ],
}

const TIPO_LABELS = {
  meta: 'meta', viaje: 'viaje', proyecto: 'proyecto',
  compromiso: 'compromiso', guardado: 'guardado', fondo: 'fondo',
}

const HERO_GRADIENT = (t, colorKey) => {
  const map = {
    terra: `linear-gradient(160deg, ${t.terra} 0%, ${t.terraDeep} 100%)`,
    sage:  `linear-gradient(160deg, ${t.sage} 0%, #4A7840 100%)`,
    gold:  `linear-gradient(160deg, ${t.gold} 0%, #906020 100%)`,
    sky:   `linear-gradient(160deg, ${t.sky} 0%, #507090 100%)`,
    lav:   `linear-gradient(160deg, ${t.lav} 0%, #7068A8 100%)`,
    mauve: `linear-gradient(160deg, ${t.mauve} 0%, #906878 100%)`,
  }
  return map[colorKey] || map.terra
}

function fechaDesde({ days, months }) {
  const d = new Date()
  if (days) d.setDate(d.getDate() + days)
  else if (months) d.setMonth(d.getMonth() + months)
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
}

function calcFechaGuardado(frecuencia, mesAnual) {
  if (frecuencia === 'semanal')    return fechaDesde({ days: 7 })
  if (frecuencia === 'mensual')    return fechaDesde({ months: 1 })
  if (frecuencia === 'trimestral') return fechaDesde({ months: 3 })
  if (frecuencia === 'semestral')  return fechaDesde({ months: 6 })
  if (frecuencia === 'anual') {
    const now = new Date()
    const d   = new Date(now.getFullYear(), mesAnual, 1)
    if (d <= now) d.setFullYear(d.getFullYear() + 1)
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`
  }
  return ''
}

const FORM_VACIO = {
  nombre: '', emoji: '✨', objetivo: '', fecha_meta: '', color: 'terra',
  acumulado_inicial: '', tipo: 'meta',
  frecuencia: 'mensual', mes_anual: 11,
  categorias_presupuesto: [],
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function Metas() {
  const {
    metas, loading, crearMeta, aportarAMeta, editarMeta, eliminarMeta,
    editarStatus, editarFase,
    fetchCategorias, crearCategoria, editarCategoria,
  } = useMetasContext()
  const { agregarGasto, editarGasto, eliminarGasto } = useGastosContext()
  const { theme } = useTheme()
  const { profile } = useProfileContext()
  const { state: locationState } = useLocation()
  const s = makeStyles(theme)
  const pronombre = profile?.genero === 'femenino' ? 'a' : profile?.genero === 'masculino' ? 'o' : ''

  const [selIdx,            setSelIdx]           = useState(0)
  const [modalCrear,        setModalCrear]        = useState(false)
  const [editandoId,        setEditandoId]        = useState(null)
  const [modalAportar,      setModalAportar]      = useState(false)
  const [modalCompletada,   setModalCompletada]   = useState(false)
  const [form,              setForm]              = useState(FORM_VACIO)
  const [montoAporte,       setMontoAporte]       = useState('')
  const [guardando,         setGuardando]         = useState(false)
  const submittingRef = useRef(false)
  const [aportarError,      setAportarError]      = useState(null)
  const [confirmarEliminar, setConfirmarEliminar] = useState(false)
  const [historial,         setHistorial]         = useState({ loading: false, aportes: [], error: null })
  const [showDatePicker,    setShowDatePicker]    = useState(false)
  const [mostrarTipoPicker, setMostrarTipoPicker] = useState(false)
  const [modalGastando,     setModalGastando]     = useState(false)
  const [modalTerminar,     setModalTerminar]     = useState(false)
  const [modalCancelar,     setModalCancelar]     = useState(false)
  const [categorias,        setCategorias]        = useState([])
  const [loadingCats,       setLoadingCats]       = useState(false)
  const [showArchivo,       setShowArchivo]       = useState(false)
  const [editAporte,        setEditAporte]        = useState(null)

  // migrate localStorage archiving to DB status
  useEffect(() => {
    const old = (() => {
      try { return JSON.parse(localStorage.getItem('florece-metas-archivadas') || '[]') }
      catch { return [] }
    })()
    if (!old.length) return
    Promise.all(old.map(id => editarStatus(id, 'terminada')))
      .then(() => localStorage.removeItem('florece-metas-archivadas'))
  }, [])

  // auto-open completada modal when navigated from Registro with a completed meta
  const handledCompletadaRef = useRef(false)
  useEffect(() => {
    if (loading || handledCompletadaRef.current) return
    const id = locationState?.completadaMetaId
    if (!id) return
    const ahorrando  = metas.filter(m => m.status === 'en_progreso' && (m.fase || 'ahorrando') === 'ahorrando')
    const gastando   = metas.filter(m => m.status === 'en_progreso' && m.fase === 'gastando')
    const archivadas = metas.filter(m => m.status === 'terminada' || m.status === 'cancelada')
    const all        = [...ahorrando, ...gastando, ...archivadas]
    const foundIdx   = all.findIndex(m => m.id === id)
    if (foundIdx >= 0) {
      setSelIdx(foundIdx)
      setModalCompletada(true)
      handledCompletadaRef.current = true
    }
  }, [metas, loading, locationState])

  const metasAhorrando  = metas.filter(m => m.status === 'en_progreso' && (m.fase || 'ahorrando') === 'ahorrando')
  const metasGastando   = metas.filter(m => m.status === 'en_progreso' && m.fase === 'gastando')
  const metasActivas    = metas.filter(m => m.status === 'en_progreso')
  const metasArchivadas = metas.filter(m => m.status === 'terminada' || m.status === 'cancelada')
  const allMetas        = [...metasAhorrando, ...metasGastando, ...metasArchivadas]
  const idx             = Math.min(selIdx, Math.max(0, allMetas.length - 1))
  const meta            = allMetas[idx] || null
  const esArchivada     = meta ? (meta.status === 'terminada' || meta.status === 'cancelada') : false

  const pct        = meta && meta.objetivo > 0
    ? Math.min(100, Math.round((meta.acumulado / meta.objetivo) * 100)) : 0
  const completada = meta ? (meta.acumulado || 0) >= meta.objetivo && meta.objetivo > 0 : false
  const falta      = meta ? Math.max(0, meta.objetivo - (meta.acumulado || 0)) : 0
  const color      = meta ? (theme[meta.color] || theme.terra) : theme.terra
  const gradient   = meta ? HERO_GRADIENT(theme, meta.color) : theme.cardGradient

  const fechaMetaDate = meta?.fecha_meta ? new Date(meta.fecha_meta + 'T12:00:00') : null
  const dias = fechaMetaDate
    ? Math.max(0, Math.ceil((fechaMetaDate - new Date()) / 86400000))
    : null
  const mesesRestantes = fechaMetaDate
    ? Math.max(1, Math.ceil((fechaMetaDate - new Date()) / (30 * 86400000)))
    : null
  const sugerenciaMensual = mesesRestantes && falta > 0
    ? Math.round(falta / mesesRestantes) : null

  const journeyMsg         = meta ? getJourneyMessage(meta.tipo || 'meta', pct) : ''
  const isGastando         = meta?.fase === 'gastando' && meta?.status === 'en_progreso'
  const gastadoDeHistorial = historial.aportes.filter(a => a.tipo === 'gasto').reduce((s, a) => s + a.monto, 0)
  const gastadoDeMeta      = isGastando ? (gastadoDeHistorial || meta?.gastado_meta || 0) : 0
  const disponible         = isGastando ? Math.max(0, (meta?.acumulado || 0) - gastadoDeMeta) : 0

  // auto-load aportes when meta changes
  useEffect(() => {
    if (!meta?.id) { setHistorial({ loading: false, aportes: [], error: null }); return }
    setHistorial({ loading: true, aportes: [], error: null })
    supabase
      .from('gastos')
      .select('id, monto, fecha, descripcion, tipo')
      .eq('meta_id', meta.id)
      .order('fecha', { ascending: false })
      .then(({ data, error }) => {
        setHistorial({ loading: false, aportes: error ? [] : (data || []), error: error || null })
      })
  }, [meta?.id])

  // lazy-load sub-categories for viaje/proyecto, with spending derived from actual gastos
  useEffect(() => {
    if (!meta || (meta.tipo !== 'viaje' && meta.tipo !== 'proyecto')) {
      setCategorias([]); return
    }
    setLoadingCats(true)
    Promise.all([
      fetchCategorias(meta.id),
      supabase.from('gastos').select('monto, meta_categoria_id').eq('meta_id', meta.id).eq('tipo', 'gasto'),
    ]).then(([{ data: cats }, { data: gastosCat }]) => {
      const gastadoMap = {}
      if (gastosCat) {
        gastosCat.forEach(g => {
          if (g.meta_categoria_id) gastadoMap[g.meta_categoria_id] = (gastadoMap[g.meta_categoria_id] || 0) + g.monto
        })
      }
      setCategorias((cats || []).map(c => ({ ...c, gastado: gastadoMap[c.id] ?? c.gastado ?? 0 })))
      setLoadingCats(false)
    })
  }, [meta?.id, meta?.tipo])

  // ── Handlers ───────────────────────────────────────────────────────────────

  function handleNuevaMeta() {
    setMostrarTipoPicker(true)
  }

  function handleTipoElegido(tipo) {
    setMostrarTipoPicker(false)
    const cats = DEFAULT_CATS[tipo] ? DEFAULT_CATS[tipo].map(c => ({ ...c })) : []
    const fecha_meta = tipo === 'guardado' ? calcFechaGuardado('mensual', 11) : ''
    setForm({ ...FORM_VACIO, tipo, categorias_presupuesto: cats, fecha_meta })
    setEditandoId(null)
    setShowDatePicker(false)
    setModalCrear(true)
  }

  async function abrirModalEditar(m) {
    setForm({
      nombre:                 m.nombre,
      emoji:                  m.emoji || '✨',
      objetivo:               String(m.objetivo),
      fecha_meta:             m.fecha_meta || '',
      color:                  m.color || 'terra',
      tipo:                   m.tipo || 'meta',
      acumulado_inicial:      '',
      frecuencia:             'mensual',
      mes_anual:              11,
      categorias_presupuesto: [],
    })
    setShowDatePicker(false)
    setEditandoId(m.id)
    setModalCrear(true)

    if (m.tipo === 'viaje' || m.tipo === 'proyecto') {
      const { data } = await fetchCategorias(m.id)
      if (data && data.length > 0) {
        const cats = data.map(c => ({
          id: c.id, nombre: c.nombre, emoji: c.emoji || '📦',
          presupuesto: String(c.presupuesto), orden: c.orden,
        }))
        setForm(f => ({ ...f, categorias_presupuesto: cats }))
      }
    }
  }

  function handleCatPresupuesto(i, val) {
    const updated = form.categorias_presupuesto.map((c, ci) =>
      ci === i ? { ...c, presupuesto: val } : c
    )
    const total = updated.reduce((sum, c) => sum + (parseFloat(c.presupuesto) || 0), 0)
    setForm(f => ({ ...f, categorias_presupuesto: updated, objetivo: total > 0 ? String(total) : '' }))
  }

  async function handleGuardarMeta() {
    if (!form.nombre || !form.objetivo) return
    setGuardando(true)

    const { acumulado_inicial, frecuencia, mes_anual, categorias_presupuesto, ...rest } = form
    const payload = {
      ...rest,
      objetivo:   parseFloat(form.objetivo),
      fecha_meta: form.fecha_meta || null,
    }

    if (editandoId) {
      await editarMeta(editandoId, payload)
      if (['viaje','proyecto'].includes(form.tipo) && categorias_presupuesto.length > 0) {
        await Promise.all(
          categorias_presupuesto
            .filter(c => c.id)
            .map(c => editarCategoria(c.id, { presupuesto: parseFloat(c.presupuesto) || 0 }))
        )
      }
    } else {
      const { data: newMeta } = await crearMeta(payload)
      setSelIdx(metasActivas.length)

      const n = parseFloat(acumulado_inicial)
      if (newMeta && n > 0 && form.tipo !== 'compromiso') {
        await Promise.all([
          aportarAMeta(newMeta.id, n),
          agregarGasto({
            monto: n, tipo: 'ahorro',
            categoria: 'Ahorro', emoji: form.emoji || '🎯',
            emocion: 'Meta',
            descripcion: `Saldo inicial · ${form.nombre}`,
            meta_id: newMeta.id,
            fecha: new Date().toISOString(),
          }),
        ])
      }

      // seed categories for viaje/proyecto
      if (newMeta && DEFAULT_CATS[form.tipo]) {
        const catsToSave = categorias_presupuesto.length > 0
          ? categorias_presupuesto.map((c, i) => ({ ...c, presupuesto: parseFloat(c.presupuesto) || 0, orden: i }))
          : DEFAULT_CATS[form.tipo].map(c => ({ ...c, presupuesto: 0 }))
        await Promise.all(catsToSave.map(cat => crearCategoria(newMeta.id, cat)))
      }
    }

    setGuardando(false)
    setModalCrear(false)
    setEditandoId(null)
    setForm(FORM_VACIO)
  }

  async function handleAportar() {
    const n = parseFloat(montoAporte)
    if (!n || n <= 0 || !meta) return
    if (submittingRef.current) return
    submittingRef.current = true
    setGuardando(true)
    try {
      const newAcumulado = (meta.acumulado || 0) + n
      const [r1, r2] = await Promise.all([
        aportarAMeta(meta.id, n),
        agregarGasto({
          monto: n, tipo: 'ahorro',
          categoria: 'Ahorro', emoji: meta.emoji || '🎯',
          emocion: 'Meta',
          descripcion: `Aporte a ${meta.nombre}`,
          meta_id: meta.id,
          fecha: new Date().toISOString(),
        }),
      ])
      if (r1?.error || r2?.error) {
        setAportarError('algo salió mal. intenta de nuevo.')
        return
      }
      setMontoAporte('')
      setAportarError(null)
      setModalAportar(false)
      // if this aporte just completed the meta, show completion popup
      if (newAcumulado >= meta.objetivo && meta.objetivo > 0 && (meta.fase || 'ahorrando') === 'ahorrando') {
        setModalCompletada(true)
      }
    } finally {
      submittingRef.current = false
      setGuardando(false)
    }
  }

  async function confirmarTerminar() {
    await editarStatus(meta.id, 'terminada')
    setModalTerminar(false)
    setSelIdx(0)
  }

  async function confirmarCancelar() {
    await editarStatus(meta.id, 'cancelada')
    setModalCancelar(false)
    setSelIdx(0)
  }

  function handleTabChange(i) {
    setSelIdx(i)
    setConfirmarEliminar(false)
  }

  const showEarlySpend = meta
    && ['meta','viaje','proyecto','guardado','fondo'].includes(meta.tipo || 'meta')
    && meta.status === 'en_progreso'
    && (meta.fase || 'ahorrando') === 'ahorrando'
    && pct > 0 && !completada

  async function handleEditAporte(id, nuevoMonto, nuevaFecha) {
    const aporte = historial.aportes.find(a => a.id === id)
    if (!aporte || !meta) return
    const delta = nuevoMonto - aporte.monto
    await editarGasto(id, { monto: nuevoMonto, fecha: new Date(nuevaFecha + 'T12:00:00').toISOString() })
    if (aporte.tipo === 'ahorro' && delta !== 0) {
      await editarMeta(meta.id, { acumulado: Math.max(0, (meta.acumulado || 0) + delta) })
    }
    setHistorial(h => ({ ...h, aportes: h.aportes.map(a => a.id === id ? { ...a, monto: nuevoMonto, fecha: new Date(nuevaFecha + 'T12:00:00').toISOString() } : a) }))
    setEditAporte(null)
  }

  async function handleDeleteAporte(id) {
    const aporte = historial.aportes.find(a => a.id === id)
    if (!aporte || !meta) return
    await eliminarGasto(id)
    if (aporte.tipo === 'ahorro') {
      await editarMeta(meta.id, { acumulado: Math.max(0, (meta.acumulado || 0) - aporte.monto) })
    }
    setHistorial(h => ({ ...h, aportes: h.aportes.filter(a => a.id !== id) }))
    setEditAporte(null)
  }

  if (loading) return <div style={s.loading}>cargando tus sueños</div>

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={s.container}>
      <p style={s.eyebrow}>tus sueños con número</p>
      <h1 style={s.title}>metas</h1>

      {allMetas.length === 0 ? (
        <div style={s.emptyState}>
          <h3 style={s.emptyTitle}>aún no tienes metas</h3>
          <p style={s.emptyText}>cada sueño grande empieza con un primer paso</p>
          <button style={s.emptyBtn} onClick={handleNuevaMeta}>crear mi primera meta ✦</button>
        </div>
      ) : (
        <>
          <div style={s.tabs}>
            <button onClick={handleNuevaMeta} style={s.tabAdd}>＋ nueva</button>
            {allMetas.map((m, i) => {
              const isSelected   = idx === i
              const isEsGastando = m.status === 'en_progreso' && m.fase === 'gastando'
              const isTerminada  = m.status === 'terminada'
              const isCancelada  = m.status === 'cancelada'
              const tabColor     = isTerminada
                ? theme.gold
                : isCancelada
                ? theme.inkLight
                : isEsGastando
                ? theme.lav
                : (theme[m.color] || theme.terra)
              return (
                <button key={m.id} onClick={() => handleTabChange(i)}
                  style={{
                    ...s.tab,
                    ...(isSelected ? { ...s.tabActive, borderColor: tabColor, color: tabColor, background: tabColor + '18' } : {}),
                    ...(isEsGastando && !isSelected ? { borderColor: theme.lav + '66', color: theme.lav + 'BB' } : {}),
                    ...(isTerminada && !isSelected ? { borderColor: theme.gold + '55', color: theme.gold + '99', opacity: 0.75 } : {}),
                    ...(isCancelada && !isSelected ? { opacity: 0.45 } : {}),
                  }}>
                  <span style={s.tabFlag}>{m.emoji}</span>
                  {m.nombre}
                  {isTerminada && <span style={{ fontSize: 9, marginTop: 1 }}> ✦</span>}
                </button>
              )
            })}
          </div>

          {meta && (
            <>
              {/* Hero card */}
              <div style={{ ...s.heroCard, background:
                esArchivada
                  ? meta.status === 'cancelada'
                    ? `linear-gradient(160deg, ${theme.blush} 0%, ${theme.terra}66 100%)`
                    : `linear-gradient(160deg, ${theme.gold}CC 0%, #906020 100%)`
                  : completada
                  ? `linear-gradient(160deg, ${theme.gold} 0%, #906020 100%)`
                  : isGastando
                  ? `linear-gradient(160deg, ${theme.lav} 0%, #7068A8 100%)`
                  : gradient
              }}>
                {!esArchivada && (
                  <div style={s.heroActions}>
                    <button style={s.heroIconBtn} onClick={() => abrirModalEditar(meta)}>✎</button>
                    <button
                      style={{ ...s.heroIconBtn, ...(confirmarEliminar ? { borderColor: 'rgba(255,100,100,0.6)', background: 'rgba(255,80,80,0.25)' } : {}) }}
                      onClick={() => {
                        if (!confirmarEliminar) { setConfirmarEliminar(true); return }
                        setConfirmarEliminar(false); eliminarMeta(meta.id)
                      }}>
                      {confirmarEliminar ? '?' : '×'}
                    </button>
                  </div>
                )}
                <div style={s.tipoBadge}>{TIPO_LABELS[meta.tipo] || 'meta'}</div>
                {completada && <div style={s.completadaBadge}>✦ meta alcanzada</div>}
                {isGastando && <div style={s.gastandoBadge}>gastando</div>}
                <p style={s.heroLabel}>{completada ? 'completada' : isGastando ? 'en fase de gasto' : 'en progreso'}</p>
                <h2 style={s.heroTitulo}>{meta.emoji} {meta.nombre}</h2>
                <p style={s.heroFecha}>
                  {dias === null ? 'sin fecha límite' : dias === 0 ? '¡hoy es el día!' : `${dias} días restantes`}
                </p>
                <div style={s.heroStats}>
                  {isGastando ? [
                    { label: 'ahorrado',   val: `$${(meta.acumulado || 0).toLocaleString('es')}` },
                    { label: 'gastado',    val: `$${gastadoDeMeta.toLocaleString('es')}` },
                    { label: 'disponible', val: `$${disponible.toLocaleString('es')}` },
                  ].map(({ label, val }, i, arr) => (
                    <div key={i} style={{ ...s.heroStat, borderRight: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.15)' : 'none' }}>
                      <div style={s.heroStatLabel}>{label}</div>
                      <div style={s.heroStatVal}>{val}</div>
                    </div>
                  )) : [
                    { label: 'ahorrado',  val: `$${(meta.acumulado || 0).toLocaleString('es')}` },
                    { label: 'objetivo',  val: `$${meta.objetivo.toLocaleString('es')}` },
                    { label: completada ? 'logrado ✦' : 'me faltan', val: completada ? '100%' : `$${falta.toLocaleString('es')}` },
                  ].map(({ label, val }, i, arr) => (
                    <div key={i} style={{ ...s.heroStat, borderRight: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.15)' : 'none' }}>
                      <div style={s.heroStatLabel}>{label}</div>
                      <div style={s.heroStatVal}>{val}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Ring section */}
              <div style={s.ringSection}>
                <div style={s.ringWrap}>
                  <ProgressRing
                    size={100} pct={pct}
                    color={completada || isGastando ? theme.gold : color}
                    strokeWidth={10}
                    gradientId={`meta-ring-${meta.id}`}
                  />
                  <div style={s.ringCenter}>
                    <div style={{ ...s.ringPct, color: completada || isGastando ? theme.gold : color }}>{pct}%</div>
                    <div style={s.ringSub}>{isGastando ? 'guardado' : 'logrado'}</div>
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  {esArchivada ? (
                    <>
                      <p style={s.ringTitulo}>
                        {meta.status === 'terminada' ? 'completada.' : 'cancelada.'}
                      </p>
                      <p style={s.ringInfo}>
                        {meta.status === 'terminada' ? journeyMsg : 'esta meta fue cancelada antes de completarse.'}
                      </p>
                    </>
                  ) : isGastando ? (
                    <>
                      <p style={s.ringTitulo}>disponible para gastar</p>
                      <p style={s.ringInfo}>tienes ${disponible.toLocaleString('es')} listos para usar en {meta.nombre}</p>
                      <button style={s.archivarBtn} onClick={() => setModalTerminar(true)}>marcar como terminada ✦</button>
                    </>
                  ) : completada ? (
                    <>
                      <p style={s.ringTitulo}>lo lograste.</p>
                      <p style={s.ringInfo}>{journeyMsg}</p>
                      {meta.tipo !== 'compromiso' && (
                        <button style={{ ...s.archivarBtn, marginBottom: 8 }} onClick={() => setModalCompletada(true)}>
                          empezar a gastar ✦
                        </button>
                      )}
                      <button style={s.archivarBtn} onClick={() => setModalTerminar(true)}>marcar como terminada ✦</button>
                    </>
                  ) : (
                    <>
                      <p style={s.ringTitulo}>
                        {pct >= 75 ? 'casi.' : pct >= 50 ? 'a la mitad.' : 'sigue.'}
                      </p>
                      <p style={s.ringInfo}>{journeyMsg}</p>
                      {sugerenciaMensual && (
                        <p style={{ ...s.ringInfo, marginTop: 4 }}>ahorra ${sugerenciaMensual.toLocaleString('es')}/mes y lo logras</p>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Sub-categorías (viaje / proyecto) */}
              {(meta.tipo === 'viaje' || meta.tipo === 'proyecto') && (
                <div style={s.catsSection}>
                  <p style={s.aportesLabel}>presupuesto por categoría</p>
                  {loadingCats ? (
                    <p style={s.historialEmpty}>cargando...</p>
                  ) : categorias.length === 0 ? (
                    <p style={s.historialEmpty}>sin categorías</p>
                  ) : (
                    <div style={s.catsList}>
                      {categorias.map(cat => {
                        const catPct = cat.presupuesto > 0 ? Math.min(100, Math.round((cat.gastado / cat.presupuesto) * 100)) : 0
                        const sobre  = cat.gastado > cat.presupuesto && cat.presupuesto > 0
                        return (
                          <div key={cat.id} style={s.catRow}>
                            <span style={s.catEmoji}>{cat.emoji}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={s.catTop}>
                                <span style={s.catNombre}>{cat.nombre}</span>
                                <span style={{ ...s.catAmounts, color: sobre ? theme.mauve : theme.inkLight }}>
                                  ${cat.gastado.toLocaleString('es')} / ${cat.presupuesto.toLocaleString('es')}
                                </span>
                              </div>
                              <div style={s.catBarBg}>
                                <div style={{ ...s.catBarFill, width: `${catPct}%`, background: sobre ? theme.mauve : color }} />
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Aportes recientes — always shown */}
              <p style={s.aportesLabel}>{isGastando ? 'movimientos' : 'aportes recientes'}</p>
              <div style={s.aportsList}>
                {historial.loading ? (
                  <p style={s.historialEmpty}>cargando...</p>
                ) : historial.error ? (
                  <p style={s.historialEmpty}>no se pudo cargar.</p>
                ) : historial.aportes.length === 0 ? (
                  <p style={s.historialEmpty}>sin movimientos registrados aún</p>
                ) : (
                  historial.aportes.slice(0, 5).map(a => (
                    <div key={a.id}
                      style={{ ...s.aportRow, cursor: (esArchivada || a.tipo === 'gasto') ? 'default' : 'pointer' }}
                      onClick={() => !esArchivada && a.tipo !== 'gasto' && setEditAporte(a)}>
                      <div style={s.aportIcon}><IcTarget size={18} /></div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={s.aportDesc}>{a.descripcion || 'movimiento'}</div>
                        <div style={s.aportFecha}>{new Date(a.fecha).toLocaleDateString('es', { day: 'numeric', month: 'short' })}</div>
                      </div>
                      <div style={{ ...s.historialMonto, color: a.tipo === 'gasto' ? theme.terra : color }}>
                        {a.tipo === 'gasto' ? '-' : '+'}${a.monto.toLocaleString('es')}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Action buttons — hidden for archived metas */}
              {!esArchivada && (
                <>
                  <div style={s.apBtns}>
                    <button style={s.apSecondary} onClick={() => abrirModalEditar(meta)}>editar</button>
                    {isGastando ? (
                      <button style={{ ...s.apPrimary, background: `linear-gradient(160deg, ${theme.lav} 0%, #7068A8 100%)` }}
                        onClick={() => setModalAportar(true)}>
                        aportar más ✦
                      </button>
                    ) : (
                      <button style={{ ...s.apPrimary, background: completada ? theme.cardGradient : gradient }}
                        onClick={() => setModalAportar(true)}>
                        {completada ? 'seguir aportando ✦' : 'aportar ✦'}
                      </button>
                    )}
                  </div>

                  {showEarlySpend && (
                    <div style={{ textAlign: 'center', marginBottom: 8 }}>
                      <button style={s.earlySpendBtn} onClick={() => setModalGastando(true)}>
                        empezar a gastar antes de llegar
                      </button>
                    </div>
                  )}

                  {meta.status === 'en_progreso' && (
                    <div style={{ textAlign: 'center', marginBottom: 16 }}>
                      <button style={s.cancelarBtn} onClick={() => setModalCancelar(true)}>
                        abandonar meta
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}

        </>
      )}

      {/* Fixed aporte FAB — replaces global FAB on /metas */}
      {meta && !esArchivada && (
        <button
          style={{
            position: 'fixed', bottom: 88,
            right: 'max(16px, calc(50% - 266px))',
            width: 58, height: 58, borderRadius: '50%',
            background: isGastando ? `linear-gradient(160deg, ${theme.lav} 0%, #7068A8 100%)` : gradient,
            border: 'none', color: 'white', fontSize: 22,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 8px 28px ${theme.accentDeep}55`,
            cursor: 'pointer', zIndex: 200,
            fontFamily: fonts.display, fontStyle: 'italic',
          }}
          onClick={() => setModalAportar(true)}
        >✦</button>
      )}

      {/* ── Modal crear / editar ───────────────────────────────────────────── */}
      {modalCrear && (
        <div style={s.overlay} onClick={() => setModalCrear(false)}>
          <div style={s.sheet} onClick={e => e.stopPropagation()}>
            <div style={s.handle} />
            <h2 style={s.sheetTitle}>
              {editandoId ? 'editar ✦' : (MODAL_TITLES[form.tipo] || 'nueva meta') + ' ✦'}
            </h2>

            {/* Nombre */}
            <div style={s.field}>
              <p style={s.fieldLabel}>{NOMBRE_LABELS[form.tipo] || '¿cuál es tu sueño?'}</p>
              <input style={s.input}
                placeholder={NOMBRE_PLACEHOLDERS[form.tipo] || ''}
                value={form.nombre}
                onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} />
            </div>

            {/* Emoji */}
            <div style={s.field}>
              <p style={s.fieldLabel}>elige un emoji</p>
              <div style={s.emojiPicker}>
                {EMOJIS.map(e => (
                  <button key={e}
                    style={{ ...s.emojiBtn, ...(form.emoji === e ? { border: `1.5px solid ${theme.terra}`, background: `${theme.terra}18` } : {}) }}
                    onClick={() => setForm(f => ({ ...f, emoji: e }))}>{e}
                  </button>
                ))}
              </div>
            </div>

            {/* Objetivo / Saldo — type-specific */}
            {form.tipo === 'compromiso' ? (
              <div style={s.field}>
                <p style={s.fieldLabel}>{editandoId ? 'actualizar saldo' : 'saldo de deuda'}</p>
                <div style={s.negativeWrap}>
                  <span style={s.negativePrefix}>−$</span>
                  <input
                    style={s.negativeInput}
                    type="text" inputMode="decimal"
                    placeholder="0"
                    value={form.objetivo}
                    onChange={e => {
                      const val = e.target.value.replace(/[^0-9.]/g, '')
                      if ((val.match(/\./g) || []).length > 1) return
                      setForm(f => ({ ...f, objetivo: val }))
                    }} />
                </div>
                <p style={s.fieldHint}>
                  {editandoId
                    ? 'ingresa el saldo restante actual de esta deuda'
                    : 'el saldo empieza negativo y avanza hacia cero a medida que pagas'}
                </p>
              </div>
            ) : ['viaje','proyecto'].includes(form.tipo) ? (
              <div style={s.field}>
                <p style={s.fieldLabel}>presupuesto por categoría</p>
                <div style={s.catBudgetList}>
                  {form.categorias_presupuesto.map((cat, i) => (
                    <div key={i} style={s.catBudgetRow}>
                      <span style={s.catBudgetEmoji}>{cat.emoji}</span>
                      <span style={s.catBudgetNombre}>{cat.nombre}</span>
                      <div style={s.catBudgetInputWrap}>
                        <span style={s.catBudgetCurrency}>$</span>
                        <input
                          style={s.catBudgetInput}
                          type="text" inputMode="decimal"
                          placeholder="0"
                          value={cat.presupuesto}
                          onChange={e => {
                            const val = e.target.value.replace(/[^0-9.]/g, '')
                            if ((val.match(/\./g) || []).length > 1) return
                            handleCatPresupuesto(i, val)
                          }} />
                      </div>
                    </div>
                  ))}
                </div>
                {parseFloat(form.objetivo) > 0 && (
                  <p style={s.catBudgetTotal}>total: ${parseFloat(form.objetivo).toLocaleString('es')}</p>
                )}
              </div>
            ) : (
              <div style={s.field}>
                <p style={s.fieldLabel}>{OBJETIVO_LABELS[form.tipo] || 'monto objetivo'}</p>
                <input
                  style={s.input}
                  type="text" inputMode="decimal"
                  placeholder="0"
                  value={form.objetivo}
                  onChange={e => {
                    const val = e.target.value.replace(/[^0-9.]/g, '')
                    if ((val.match(/\./g) || []).length > 1) return
                    setForm(f => ({ ...f, objetivo: val }))
                  }} />
              </div>
            )}

            {/* Ya tengo — not for compromiso */}
            {form.tipo !== 'compromiso' && !editandoId && (
              <div style={s.field}>
                <p style={s.fieldLabel}>ya tengo <span style={s.optional}>(opcional)</span></p>
                <input
                  style={s.input}
                  type="text" inputMode="decimal"
                  placeholder="0 — si ya tienes algo ahorrado"
                  value={form.acumulado_inicial}
                  onChange={e => {
                    const val = e.target.value.replace(/[^0-9.]/g, '')
                    if ((val.match(/\./g) || []).length > 1) return
                    setForm(f => ({ ...f, acumulado_inicial: val }))
                  }} />
              </div>
            )}

            {/* Fecha / Frecuencia */}
            {form.tipo === 'guardado' && !editandoId ? (
              <>
                <div style={s.field}>
                  <p style={s.fieldLabel}>frecuencia del ciclo</p>
                  <div style={s.fechaPresets}>
                    {FRECUENCIAS.map(f => (
                      <button key={f.value}
                        style={{ ...s.fechaPresetBtn, ...(form.frecuencia === f.value ? { borderColor: theme.accent, background: theme.accent + '18', color: theme.accent } : {}) }}
                        onClick={() => {
                          const newFecha = calcFechaGuardado(f.value, form.mes_anual)
                          setForm(prev => ({ ...prev, frecuencia: f.value, fecha_meta: newFecha }))
                        }}>{f.label}
                      </button>
                    ))}
                  </div>
                </div>
                {form.frecuencia === 'anual' && (
                  <div style={s.field}>
                    <p style={s.fieldLabel}>mes del ciclo</p>
                    <div style={s.fechaPresets}>
                      {MESES.map((mes, i) => (
                        <button key={i}
                          style={{ ...s.fechaPresetBtn, ...(form.mes_anual === i ? { borderColor: theme.accent, background: theme.accent + '18', color: theme.accent } : {}) }}
                          onClick={() => {
                            const newFecha = calcFechaGuardado('anual', i)
                            setForm(prev => ({ ...prev, mes_anual: i, fecha_meta: newFecha }))
                          }}>{mes}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div style={s.field}>
                <p style={s.fieldLabel}>
                  {form.tipo === 'compromiso' ? 'fecha límite de pago' : 'fecha objetivo'}
                  {form.tipo !== 'compromiso' && <span style={s.optional}> (opcional)</span>}
                </p>
                <div style={s.fechaPresets}>
                  {form.tipo !== 'compromiso' && (
                    <button
                      style={{ ...s.fechaPresetBtn, ...(!form.fecha_meta && !showDatePicker ? { borderColor: theme.accent, background: theme.accent + '18', color: theme.accent } : {}) }}
                      onClick={() => { setForm(f => ({ ...f, fecha_meta: '' })); setShowDatePicker(false) }}>
                      sin fecha
                    </button>
                  )}
                  {FECHA_PRESETS.map(p => {
                    const val = fechaDesde(p)
                    const active = form.fecha_meta === val && !showDatePicker
                    return (
                      <button key={p.label}
                        style={{ ...s.fechaPresetBtn, ...(active ? { borderColor: theme.accent, background: theme.accent + '18', color: theme.accent } : {}) }}
                        onClick={() => { setForm(f => ({ ...f, fecha_meta: val })); setShowDatePicker(false) }}>
                        {p.label}
                      </button>
                    )
                  })}
                  <button
                    style={{ ...s.fechaPresetBtn, ...(showDatePicker ? { borderColor: theme.accent, background: theme.accent + '18', color: theme.accent } : {}) }}
                    onClick={() => setShowDatePicker(true)}>
                    elegir fecha
                  </button>
                </div>
                {showDatePicker && (
                  <input style={{ ...s.input, marginTop: 8 }} type="date"
                    value={form.fecha_meta}
                    onChange={e => setForm(f => ({ ...f, fecha_meta: e.target.value }))} />
                )}
              </div>
            )}

            {/* Color */}
            <div style={s.field}>
              <p style={s.fieldLabel}>color</p>
              <div style={s.colorPicker}>
                {COLORES.map(c => (
                  <button key={c}
                    style={{ ...s.colorDot, background: theme[c],
                      ...(form.color === c ? { outline: `3px solid ${theme[c]}`, outlineOffset: 2, transform: 'scale(1.15)' } : {}) }}
                    onClick={() => setForm(f => ({ ...f, color: c }))} />
                ))}
              </div>
            </div>

            <button
              style={{ ...s.sheetBtn, opacity: (!form.nombre || !form.objetivo) ? 0.5 : 1 }}
              onClick={handleGuardarMeta}
              disabled={!form.nombre || !form.objetivo || guardando}>
              {guardando ? 'guardando...' : editandoId ? 'guardar cambios ✦' : 'crear ✦'}
            </button>
          </div>
        </div>
      )}

      {/* ── Modal aportar ─────────────────────────────────────────────────── */}
      {modalAportar && meta && (
        <div style={s.overlay} onClick={() => { setModalAportar(false); setAportarError(null) }}>
          <div style={s.sheet} onClick={e => e.stopPropagation()}>
            <div style={s.handle} />
            <h2 style={s.sheetTitle}>aportar a {meta.nombre}</h2>
            <div style={s.field}>
              <p style={s.fieldLabel}>¿cuánto quieres aportar?</p>
              <input
                style={s.input} type="text" inputMode="decimal" placeholder="0"
                value={montoAporte}
                onChange={e => {
                  const val = e.target.value.replace(/[^0-9.]/g, '')
                  if ((val.match(/\./g) || []).length > 1) return
                  setMontoAporte(val)
                }} />
            </div>
            {aportarError && <p style={s.aportarErrorText}>{aportarError}</p>}
            <button
              style={{ ...s.sheetBtn, opacity: (!montoAporte || parseFloat(montoAporte) <= 0) ? 0.5 : 1 }}
              onClick={handleAportar}
              disabled={!montoAporte || parseFloat(montoAporte) <= 0 || guardando}>
              {guardando ? 'guardando...' : 'confirmar aporte ✦'}
            </button>
          </div>
        </div>
      )}

      {/* ── Modal: meta completada al 100% ────────────────────────────────── */}
      {modalCompletada && meta && (
        <div style={s.overlay} onClick={() => setModalCompletada(false)}>
          <div style={s.sheet} onClick={e => e.stopPropagation()}>
            <div style={s.handle} />
            <h2 style={s.sheetTitle}>
              {meta.tipo === 'compromiso' ? '¡deuda saldada! ✦' : '✦ lo lograste'}
            </h2>
            <p style={s.warningBody}>
              {meta.tipo === 'compromiso'
                ? `alcanzaste el 100% de tu compromiso ${meta.nombre}. esa carga ya no es tuya.`
                : `alcanzaste el 100% de tu meta ${meta.nombre}. es tuyo — ¿qué quieres hacer ahora?`}
            </p>
            {meta.tipo === 'compromiso' ? (
              <>
                <button style={s.sheetBtn} onClick={async () => {
                  await editarStatus(meta.id, 'terminada')
                  setModalCompletada(false)
                  setSelIdx(0)
                }}>marcar como saldado ✦</button>
                <button style={s.warningConfirmBtn} onClick={() => setModalCompletada(false)}>seguir registrando pagos</button>
              </>
            ) : (
              <>
                <button style={s.sheetBtn} onClick={async () => {
                  await editarFase(meta.id, 'gastando')
                  setModalCompletada(false)
                }}>empezar a gastar ✦</button>
                <button style={s.warningConfirmBtn} onClick={() => setModalCompletada(false)}>esperar para usarlo</button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Modal empezar a gastar antes — passive-aggressive ─────────────── */}
      {modalGastando && (
        <div style={s.overlay} onClick={() => setModalGastando(false)}>
          <div style={s.sheet} onClick={e => e.stopPropagation()}>
            <div style={s.handle} />
            <h2 style={s.sheetTitle}>espera un momento.</h2>
            <p style={s.warningBody}>
              todavía no llegaste al objetivo. si empiezas a gastar ahora,
              estás tomando prestado de tu propio sueño.
            </p>
            <p style={s.warningSubtext}>
              las personas que esperan a completar su meta terminan mucho más
              satisfechas. ¿seguro que quieres hacerlo ahora?
            </p>
            <button style={s.sheetBtn} onClick={() => setModalGastando(false)}>
              espera, tienes razón
            </button>
            <button style={s.warningConfirmBtn} onClick={async () => {
              await editarFase(meta.id, 'gastando')
              setModalGastando(false)
            }}>
              entiendo, quiero hacerlo de todas formas
            </button>
          </div>
        </div>
      )}

      {/* ── Modal marcar terminada ────────────────────────────────────────── */}
      {modalTerminar && (
        <div style={s.overlay} onClick={() => setModalTerminar(false)}>
          <div style={s.sheet} onClick={e => e.stopPropagation()}>
            <div style={s.handle} />
            <h2 style={s.sheetTitle}>¡lo lograste! ✦</h2>
            <p style={s.warningSubtext}>esta meta pasará al archivo. siempre podrás verla ahí.</p>
            <button style={s.sheetBtn} onClick={confirmarTerminar}>sí, marcar como terminada ✦</button>
            <button style={s.warningConfirmBtn} onClick={() => setModalTerminar(false)}>todavía no</button>
          </div>
        </div>
      )}

      {/* ── Modal cancelar meta ───────────────────────────────────────────── */}
      {modalCancelar && (
        <div style={s.overlay} onClick={() => setModalCancelar(false)}>
          <div style={s.sheet} onClick={e => e.stopPropagation()}>
            <div style={s.handle} />
            <h2 style={s.sheetTitle}>¿abandonar esta meta?</h2>
            <p style={s.warningSubtext}>
              se guardará como cancelada. los aportes registrados se mantienen en tu historial.
            </p>
            <button style={s.sheetBtn} onClick={() => setModalCancelar(false)}>volver</button>
            <button style={s.warningConfirmBtn} onClick={confirmarCancelar}>sí, abandonar</button>
          </div>
        </div>
      )}

      {/* ── Modal editar aporte ──────────────────────────────────────────── */}
      {editAporte && (
        <EditAporteModal
          aporte={editAporte}
          onSave={handleEditAporte}
          onDelete={handleDeleteAporte}
          onClose={() => setEditAporte(null)}
        />
      )}

      {/* ── Type picker ───────────────────────────────────────────────────── */}
      {mostrarTipoPicker && (
        <MetaTipoPicker onSelect={handleTipoElegido} onClose={() => setMostrarTipoPicker(false)} />
      )}
    </div>
  )
}

// ── EditAporteModal ────────────────────────────────────────────────────────────

function EditAporteModal({ aporte, onSave, onDelete, onClose }) {
  const { theme } = useTheme()
  const [monto,   setMonto]   = useState(String(aporte.monto))
  const [fecha,   setFecha]   = useState(() => {
    const d = new Date(aporte.fecha)
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
  })
  const [confirm, setConfirm] = useState(false)
  const [saving,  setSaving]  = useState(false)

  async function handleSave() {
    const n = parseFloat(monto)
    if (!n || n <= 0) return
    setSaving(true)
    await onSave(aporte.id, n, fecha)
    setSaving(false)
  }

  async function handleDelete() {
    if (!confirm) { setConfirm(true); return }
    setSaving(true)
    await onDelete(aporte.id)
    setSaving(false)
  }

  const s = {
    overlay:  { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 350, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' },
    sheet:    { background: theme.paper, borderRadius: '24px 24px 0 0', padding: '20px 24px 48px', width: '100%', maxWidth: 480 },
    handle:   { width: 40, height: 4, borderRadius: 2, background: theme.blush, margin: '0 auto 18px' },
    title:    { fontFamily: fonts.display, fontSize: 20, fontWeight: 400, color: theme.ink, margin: '0 0 16px' },
    label:    { fontSize: 11, letterSpacing: 1.2, textTransform: 'uppercase', color: theme.inkLight, fontWeight: 500, margin: '0 0 6px', fontFamily: fonts.body },
    input:    { width: '100%', padding: '11px 13px', borderRadius: 12, border: `1.5px solid ${theme.cardBorder}`, background: theme.inputBg, fontFamily: fonts.body, fontSize: 15, color: theme.ink, outline: 'none', boxSizing: 'border-box', marginBottom: 14 },
    saveBtn:  { width: '100%', padding: 14, borderRadius: 18, border: 'none', background: theme.cardGradient, color: 'white', fontFamily: fonts.display, fontSize: 18, fontStyle: 'italic', cursor: 'pointer', marginTop: 4 },
    delBtn:   { width: '100%', padding: 11, borderRadius: 14, border: `1px solid ${theme.cardBorder}`, background: 'transparent', fontFamily: fonts.body, fontSize: 13, color: confirm ? theme.mauve : theme.inkLight, cursor: 'pointer', marginTop: 8 },
  }

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.sheet} onClick={e => e.stopPropagation()}>
        <div style={s.handle} />
        <h2 style={s.title}>{aporte.tipo === 'gasto' ? 'editar gasto' : 'editar aporte'}</h2>
        <p style={s.label}>monto</p>
        <input style={s.input} type="text" inputMode="decimal" value={monto}
          onChange={e => { const v = e.target.value.replace(/[^0-9.]/g, ''); if ((v.match(/\./g)||[]).length <= 1) setMonto(v) }} />
        <p style={s.label}>fecha</p>
        <input style={s.input} type="date" value={fecha} onChange={e => setFecha(e.target.value)} />
        <button style={s.saveBtn} onClick={handleSave} disabled={saving}>
          {saving ? 'guardando...' : 'guardar cambios ✦'}
        </button>
        <button style={s.delBtn} onClick={handleDelete} disabled={saving}>
          {confirm ? '¿confirmar eliminación?' : 'eliminar'}
        </button>
      </div>
    </div>
  )
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const makeStyles = (t) => ({
  container:    { maxWidth: 560, margin: '0 auto', padding: '56px 28px 140px', fontFamily: fonts.body, background: t.cream, minHeight: '100vh' },
  loading:      { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: fonts.body, fontSize: 15, color: t.inkLight },
  eyebrow:      { fontFamily: fonts.script, fontStyle: 'italic', fontSize: 15, color: t.inkLight, margin: '0 0 4px' },
  title:        { fontFamily: fonts.display, fontSize: 36, fontWeight: 300, color: t.ink, margin: '0 0 24px' },

  emptyState:   { textAlign: 'center', padding: '48px 24px', background: t.paper, borderRadius: 24, border: `1.5px solid ${t.cardBorder}` },
  emptyTitle:   { fontFamily: fonts.display, fontSize: 22, fontWeight: 400, color: t.ink, margin: '0 0 10px' },
  emptyText:    { fontFamily: fonts.script, fontStyle: 'italic', fontSize: 15, color: t.inkLight, lineHeight: 1.6, margin: '0 0 24px' },
  emptyBtn:     { padding: '14px 28px', borderRadius: 20, border: 'none', background: t.cardGradient, color: 'white', fontFamily: fonts.display, fontSize: 18, fontStyle: 'italic', cursor: 'pointer' },

  tabs:         { display: 'flex', gap: 8, marginBottom: 20, overflowX: 'auto', scrollbarWidth: 'none', paddingBottom: 4 },
  tab:          { flex: '0 0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, padding: '10px 14px', borderRadius: 16, border: `1.5px solid ${t.cardBorder}`, background: t.paper, cursor: 'pointer', fontSize: 13, color: t.inkMid, fontFamily: fonts.body },
  tabActive:    { fontWeight: 600 },
  tabFlag:      { fontSize: 20, display: 'block' },
  tabAdd:       { flex: '0 0 auto', padding: '10px 16px', borderRadius: 16, border: `2px dashed ${t.accent}55`, background: 'transparent', cursor: 'pointer', color: t.accent, fontFamily: fonts.body, fontSize: 13 },

  heroCard:        { borderRadius: 24, padding: '24px 22px', marginBottom: 16, boxShadow: '0 16px 40px rgba(0,0,0,0.2)', position: 'relative', overflow: 'hidden' },
  heroActions:     { position: 'absolute', top: 14, right: 14, display: 'flex', gap: 6 },
  heroIconBtn:     { width: 30, height: 30, borderRadius: 8, border: '1px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.15)', color: 'white', fontSize: 15, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  tipoBadge:       { display: 'inline-block', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 20, padding: '2px 10px', fontFamily: fonts.body, fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)', marginBottom: 6 },
  completadaBadge: { display: 'inline-block', background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 20, padding: '3px 12px', marginBottom: 8, fontFamily: fonts.script, fontStyle: 'italic', fontSize: 13, color: 'white' },
  gastandoBadge:   { display: 'inline-block', background: 'rgba(180,168,200,0.25)', border: '1px solid rgba(180,168,200,0.5)', borderRadius: 20, padding: '3px 12px', marginBottom: 8, fontFamily: fonts.script, fontStyle: 'italic', fontSize: 13, color: 'rgba(220,210,240,0.9)' },
  heroLabel:       { fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginBottom: 6 },
  heroTitulo:      { fontFamily: fonts.display, fontSize: 32, fontWeight: 300, color: 'white', marginBottom: 4 },
  heroFecha:       { fontFamily: fonts.body, fontSize: 14, color: 'rgba(255,255,255,0.75)', marginBottom: 20 },
  heroStats:       { display: 'flex', background: 'rgba(255,255,255,0.12)', borderRadius: 16, overflow: 'hidden', backdropFilter: 'blur(10px)' },
  heroStat:        { flex: 1, padding: '14px 12px' },
  heroStatLabel:   { fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)', marginBottom: 4 },
  heroStatVal:     { fontFamily: fonts.display, fontSize: 18, fontWeight: 600, color: 'white' },

  ringSection:  { display: 'flex', alignItems: 'center', gap: 20, background: t.paper, border: `1px solid ${t.blush}44`, borderRadius: 22, padding: 20, marginBottom: 16 },
  ringWrap:     { position: 'relative', flexShrink: 0 },
  ringCenter:   { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' },
  ringPct:      { fontFamily: fonts.display, fontSize: 24, fontWeight: 600, lineHeight: 1 },
  ringSub:      { fontFamily: fonts.body, fontSize: 12, color: t.inkLight, marginTop: 2 },
  ringTitulo:   { fontFamily: fonts.display, fontSize: 18, color: t.ink, marginBottom: 4 },
  ringInfo:     { fontFamily: fonts.script, fontStyle: 'italic', fontSize: 14, color: t.inkLight, marginBottom: 6, lineHeight: 1.5 },
  archivarBtn:  { padding: '9px 18px', borderRadius: 14, border: `1.5px solid ${t.gold}88`, background: t.gold + '18', fontFamily: fonts.display, fontSize: 15, fontStyle: 'italic', cursor: 'pointer', color: t.gold, display: 'block', marginBottom: 6 },

  catsSection:  { background: t.paper, border: `1px solid ${t.cardBorder}`, borderRadius: 22, padding: '16px 18px', marginBottom: 16 },
  catsList:     { display: 'flex', flexDirection: 'column', gap: 12 },
  catRow:       { display: 'flex', alignItems: 'center', gap: 10 },
  catEmoji:     { fontSize: 20, flexShrink: 0 },
  catTop:       { display: 'flex', justifyContent: 'space-between', marginBottom: 4 },
  catNombre:    { fontFamily: fonts.body, fontSize: 14, fontWeight: 500, color: t.ink },
  catAmounts:   { fontFamily: fonts.body, fontSize: 12 },
  catBarBg:     { height: 4, borderRadius: 2, background: t.blush + '44', overflow: 'hidden' },
  catBarFill:   { height: '100%', borderRadius: 2, transition: 'width 0.3s' },

  aportesLabel: { fontFamily: fonts.body, fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase', color: t.inkLight, fontWeight: 500, margin: '0 0 10px' },
  aportsList:   { display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 },
  aportRow:     { padding: '12px 14px', background: t.paper, border: `1.5px solid ${t.txBorder}`, borderRadius: 14, display: 'flex', alignItems: 'center', gap: 12 },
  aportIcon:    { width: 34, height: 34, borderRadius: 10, background: t.gold + '2E', display: 'flex', alignItems: 'center', justifyContent: 'center', color: t.gold, flexShrink: 0 },
  aportDesc:    { fontFamily: fonts.body, fontSize: 14, fontWeight: 500, color: t.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  aportFecha:   { fontFamily: fonts.body, fontSize: 13, color: t.inkLight, marginTop: 1 },
  historialMonto:{ fontFamily: fonts.display, fontSize: 16, fontWeight: 600, flexShrink: 0 },
  historialEmpty:{ fontFamily: fonts.script, fontStyle: 'italic', fontSize: 14, color: t.inkLight, textAlign: 'center', padding: '8px 0', margin: 0 },

  apBtns:      { display: 'flex', gap: 8, marginBottom: 12 },
  apPrimary:   { flex: 1, padding: 13, borderRadius: 16, border: 'none', color: 'white', fontFamily: fonts.display, fontSize: 16, fontStyle: 'italic', cursor: 'pointer', boxShadow: `0 10px 28px ${t.accentDeep}44` },
  apSecondary: { flex: 1, padding: 13, borderRadius: 16, background: t.paper, border: `1.5px solid ${t.cardBorder}`, color: t.inkMid, fontFamily: fonts.body, fontSize: 13, cursor: 'pointer' },

  earlySpendBtn: { background: 'none', border: 'none', fontFamily: fonts.script, fontStyle: 'italic', fontSize: 13, color: t.inkLight, textDecoration: 'underline', cursor: 'pointer', padding: '4px 0' },
  cancelarBtn:   { background: 'none', border: 'none', fontFamily: fonts.script, fontStyle: 'italic', fontSize: 13, color: t.inkLight, textDecoration: 'underline', cursor: 'pointer', padding: '4px 0' },


  overlay:      { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 300, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' },
  sheet:        { background: t.paper, borderRadius: '28px 28px 0 0', padding: '20px 24px 48px', width: '100%', maxWidth: 480, maxHeight: '92vh', overflowY: 'auto' },
  handle:       { width: 40, height: 4, borderRadius: 2, background: t.blush, margin: '0 auto 20px' },
  sheetTitle:   { fontFamily: fonts.display, fontSize: 24, fontWeight: 400, color: t.ink, margin: '0 0 16px' },
  field:        { marginBottom: 16 },
  fieldLabel:   { fontSize: 11, letterSpacing: 1.2, textTransform: 'uppercase', color: t.inkLight, fontWeight: 500, margin: '0 0 8px', fontFamily: fonts.body },
  optional:     { fontWeight: 400, textTransform: 'none', letterSpacing: 0, fontFamily: fonts.script, fontStyle: 'italic', fontSize: 13, color: t.inkLight },
  fieldHint:    { fontFamily: fonts.script, fontStyle: 'italic', fontSize: 12, color: t.inkLight, margin: '6px 0 0', lineHeight: 1.4 },
  input:        { width: '100%', padding: '12px 14px', borderRadius: 14, border: `1.5px solid ${t.cardBorder}`, background: t.inputBg, fontFamily: fonts.body, fontSize: 15, color: t.ink, outline: 'none', boxSizing: 'border-box', MozAppearance: 'textfield' },
  negativeWrap: { display: 'flex', alignItems: 'center', border: `1.5px solid ${t.cardBorder}`, borderRadius: 14, background: t.inputBg, overflow: 'hidden' },
  negativePrefix:{ padding: '12px 4px 12px 14px', fontFamily: fonts.display, fontSize: 18, color: t.mauve, flexShrink: 0 },
  negativeInput: { flex: 1, padding: '12px 14px 12px 4px', border: 'none', background: 'transparent', fontFamily: fonts.display, fontSize: 18, color: t.ink, outline: 'none', MozAppearance: 'textfield' },
  catBudgetList: { display: 'flex', flexDirection: 'column', gap: 8 },
  catBudgetRow:  { display: 'flex', alignItems: 'center', gap: 10 },
  catBudgetEmoji:{ fontSize: 18, flexShrink: 0 },
  catBudgetNombre:{ fontFamily: fonts.body, fontSize: 14, color: t.ink, flex: 1 },
  catBudgetInputWrap:{ display: 'flex', alignItems: 'center', gap: 4, border: `1.5px solid ${t.cardBorder}`, borderRadius: 10, background: t.inputBg, padding: '6px 10px', width: 100, flexShrink: 0 },
  catBudgetCurrency:{ fontFamily: fonts.body, fontSize: 14, color: t.inkLight },
  catBudgetInput:{ border: 'none', background: 'transparent', fontFamily: fonts.body, fontSize: 14, color: t.ink, outline: 'none', width: '100%', MozAppearance: 'textfield' },
  catBudgetTotal:{ fontFamily: fonts.script, fontStyle: 'italic', fontSize: 14, color: t.inkMid, margin: '10px 0 0', textAlign: 'right' },
  fechaPresets: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  fechaPresetBtn:{ padding: '7px 14px', borderRadius: 20, border: `1.5px solid ${t.cardBorder}`, background: t.paper, cursor: 'pointer', fontSize: 13, color: t.inkMid, fontFamily: fonts.body },
  emojiPicker:  { display: 'flex', flexWrap: 'wrap', gap: 8 },
  emojiBtn:     { fontSize: 22, padding: '8px', borderRadius: 12, border: `1.5px solid ${t.cardBorder}`, background: t.inputBg, cursor: 'pointer' },
  colorPicker:  { display: 'flex', gap: 12, alignItems: 'center' },
  colorDot:     { width: 28, height: 28, borderRadius: '50%', border: 'none', cursor: 'pointer', transition: 'all 0.2s', flexShrink: 0 },
  sheetBtn:     { width: '100%', padding: '16px', borderRadius: 20, border: 'none', background: t.cardGradient, color: 'white', fontFamily: fonts.display, fontSize: 20, fontStyle: 'italic', cursor: 'pointer', marginTop: 8, boxShadow: `0 8px 24px ${t.accentDeep}44` },
  aportarErrorText:{ fontFamily: fonts.script, fontStyle: 'italic', fontSize: 14, color: t.mauve, textAlign: 'center', margin: '0 0 8px' },
  warningBody:     { fontFamily: fonts.display, fontStyle: 'italic', fontSize: 20, fontWeight: 300, color: t.ink, lineHeight: 1.4, margin: '0 0 14px' },
  warningSubtext:  { fontFamily: fonts.script, fontStyle: 'italic', fontSize: 15, color: t.inkMid, lineHeight: 1.6, margin: '0 0 20px' },
  warningConfirmBtn:{ width: '100%', padding: '12px', borderRadius: 16, border: `1px solid ${t.cardBorder}`, background: 'transparent', fontFamily: fonts.body, fontSize: 13, cursor: 'pointer', color: t.inkLight, marginTop: 8 },
})
